import { memo } from 'react';

import { cn } from '../../../../utils/cn';

interface BucketButtonProps {
  /** Zero-based bucket index (0–99). Displayed as `index + 1`. */
  index: number;
  /** Whether this bucket is part of the committed selection. */
  isActive: boolean;
  /**
   * Whether this bucket is being previewed as part of an in-progress
   * drag or click-range selection.
   */
  isPreview: boolean;
  /** Whether this is the anchor bucket of the current click-range selection. */
  isStart: boolean;
  /** Optional UI color for this bucket when it belongs to a specific range. */
  rangeColor?: string;
  /** Whether the entire input is disabled. */
  disabled: boolean;
  /** Called with `index` when the primary mouse button is pressed on this cell. */
  onMouseDown: (index: number) => void;
  /** Called with `index` when the pointer enters this cell. */
  onMouseEnter: (index: number) => void;
}

/**
 * A single cell in the 10×10 bucket selection grid.
 *
 * Wrapped in `memo` because the parent re-renders on every pointer event
 * (hover, drag) and there are 100 instances — bailing out of unchanged cells
 * is critical for smooth interaction.
 */
export const BucketButton = memo(function BucketButton({
  index,
  isActive,
  isPreview,
  isStart,
  rangeColor,
  disabled,
  onMouseDown,
  onMouseEnter,
}: BucketButtonProps) {
  return (
    <button
      title={`Bucket ${index + 1}`}
      aria-label={`Bucket ${index + 1}`}
      className={cn(
        'size-6 rounded-full text-[10px] transition-colors duration-200 ease-in-out select-none',
        {
          'ring-2 ring-primary ring-offset-1': isStart,
          'cursor-not-allowed opacity-50': disabled,
          'bg-accent/80 text-accent-foreground': isPreview,
          'bg-accent text-accent-foreground': !isPreview && isActive && !rangeColor,
          'bg-muted text-muted-foreground hover:bg-muted/50': !isPreview && !isActive,
        },
      )}
      style={
        !isPreview && isActive && rangeColor
          ? { backgroundColor: rangeColor, color: '#fff' }
          : undefined
      }
      disabled={disabled}
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onMouseDown(index);
      }}
      onMouseEnter={() => onMouseEnter(index)}
    >
      {index + 1}
    </button>
  );
});
